import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import axios from 'axios';

export interface User {
  adminId: string;
  tenantId: string;
  tenantName: string;
  isGlobalAdmin: boolean;
  username: string;
}

interface AuthContextType {
  isAuthenticated: boolean;
  token: string | null;
  adminId: string | null;
  tenantId: string | null;
  tenantName: string | null;
  isGlobalAdmin: boolean;
  isLoading: boolean;
  user: User | null;
  setTenantId: (tenantId: string | null) => void;
  login: (username: string, password: string) => Promise<{ requiresTotp: boolean; adminId?: string }>;
  verifyTotp: (adminId: string, token: string) => Promise<string>;
  verifyOtp: (username: string, otp: string) => Promise<{ token: string; adminId: string }>;
  requestOtp: (username: string, method: 'email' | 'sms') => Promise<{ message: string }>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const API_BASE_URL = '/api/v1';

// Enable sending cookies with every request
axios.defaults.withCredentials = true;

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  const [adminId, setAdminId] = useState<string | null>(null);
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [tenantName, setTenantName] = useState<string | null>(null);
  const [isGlobalAdmin, setIsGlobalAdmin] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadAuth = () => {
      const storedAdminId = localStorage.getItem('admin_id');
      const storedTenantId = localStorage.getItem('tenant_id');
      const storedTenantName = localStorage.getItem('tenant_name');
      const storedIsGlobalAdmin = localStorage.getItem('is_global_admin') === 'true';
      const storedUsername = localStorage.getItem('username');
      const authPresent = localStorage.getItem('auth_present') === 'true';

      // Clean up invalid tenant_id if it was saved as string "null"
      if (storedTenantId === 'null') {
        localStorage.removeItem('tenant_id');
      }

      if (authPresent && storedAdminId) {
        const validTenantId = storedTenantId === 'null' ? null : storedTenantId;
        setAdminId(storedAdminId);
        setTenantId(validTenantId);
        setTenantName(storedTenantName);
        setIsGlobalAdmin(storedIsGlobalAdmin);
        setUser({
          adminId: storedAdminId,
          tenantId: validTenantId || '',
          tenantName: storedTenantName || validTenantId || '',
          isGlobalAdmin: storedIsGlobalAdmin,
          username: storedUsername || storedAdminId,
        });
        // Token is managed by httpOnly cookie; this satisfies component guards
        setToken('cookie');
        setIsAuthenticated(true);
      }
      setIsLoading(false);
    };

    loadAuth();
  }, []);

  const persistAuthState = (data: { token: string; adminId: string; tenantId?: string; tenantName?: string; isGlobalAdmin: boolean; username?: string }) => {
    setToken(data.token);
    setAdminId(data.adminId);
    setTenantId(data.tenantId || null);
    setTenantName(data.tenantName || null);
    setIsGlobalAdmin(data.isGlobalAdmin);
    setUser({
      adminId: data.adminId,
      tenantId: data.tenantId || '',
      tenantName: data.tenantName || data.tenantId || '',
      isGlobalAdmin: data.isGlobalAdmin,
      username: data.username || data.adminId,
    });
    setIsAuthenticated(true);

    // Store non-secret auth metadata in localStorage for UI state recovery
    localStorage.setItem('auth_present', 'true');
    localStorage.setItem('admin_id', data.adminId);
    if (data.tenantId && data.tenantId !== 'null') {
      localStorage.setItem('tenant_id', data.tenantId);
    } else {
      localStorage.removeItem('tenant_id');
    }
    if (data.tenantName) {
      localStorage.setItem('tenant_name', data.tenantName);
    } else {
      localStorage.removeItem('tenant_name');
    }
    localStorage.setItem('is_global_admin', data.isGlobalAdmin.toString());
    if (data.username) {
      localStorage.setItem('username', data.username);
    } else {
      localStorage.removeItem('username');
    }
  };

  const login = async (username: string, password: string): Promise<{ requiresTotp: boolean; adminId?: string }> => {
    const response = await axios.post(`${API_BASE_URL}/auth/login`, { username, password });
    if (response.data.requiresTotp) {
      // Store for TOTP verification
      sessionStorage.setItem('pending_admin_id', response.data.adminId);
      sessionStorage.setItem('pending_tenant_id', response.data.tenantId);
      sessionStorage.setItem('pending_is_global_admin', response.data.isGlobalAdmin.toString());
      return { requiresTotp: true, adminId: response.data.adminId };
    }
    persistAuthState(response.data);
    return { requiresTotp: false, adminId: response.data.adminId };
  };

  const verifyTotp = async (adminId: string, token: string): Promise<string> => {
    const response = await axios.post(`${API_BASE_URL}/auth/totp/verify`, { adminId: parseInt(adminId), token });
    persistAuthState(response.data);
    return response.data.token;
  };

  const verifyOtp = async (username: string, otp: string): Promise<{ token: string; adminId: string }> => {
    const response = await axios.post(`${API_BASE_URL}/auth/otp/verify`, { username, otp });
    persistAuthState(response.data);
    return response.data;
  };

  const requestOtp = async (username: string, method: 'email' | 'sms'): Promise<{ message: string }> => {
    const response = await axios.post(`${API_BASE_URL}/auth/otp/request`, { username, method });
    return response.data;
  };

  const logout = async () => {
    try {
      // Call backend to clear httpOnly cookie
      await axios.post(`${API_BASE_URL}/auth/logout`);
    } catch {
      // Ignore errors — clear local state regardless
    }

    setToken(null);
    setAdminId(null);
    setTenantId(null);
    setTenantName(null);
    setIsGlobalAdmin(false);
    setUser(null);
    setIsAuthenticated(false);

    localStorage.removeItem('auth_present');
    localStorage.removeItem('admin_id');
    localStorage.removeItem('tenant_id');
    localStorage.removeItem('tenant_name');
    localStorage.removeItem('is_global_admin');
    localStorage.removeItem('username');
    sessionStorage.removeItem('selectedTenantId');
    delete axios.defaults.headers['X-Selected-Tenant-ID'];
  };

  return (
    <AuthContext.Provider value={{
      isAuthenticated,
      token,
      adminId,
      tenantId,
      tenantName,
      isGlobalAdmin,
      isLoading,
      user,
      setTenantId,
      login,
      verifyTotp,
      verifyOtp,
      requestOtp,
      logout,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
